import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import { ApiError } from '@/lib/api/errors';
import { auditKeys } from '@/features/audit/keys';
import { runKeys } from '@/features/runs/api';

export type ModelOptions = Schemas['ModelOptionsDto'];
export type ModelOption = Schemas['ModelOptionDto'];

export const modelKeys = {
  all: ['model'] as const,
  switch: ['model', 'switch'] as const,
};

/** The configured AI models, which one runs use, and who chose it. */
export const modelOptionsQuery = () =>
  queryOptions({
    queryKey: modelKeys.all,
    queryFn: () => unwrap(api.GET('/api/scraping/model')),
    staleTime: 30_000,
  });

export const useModelOptions = () => useQuery(modelOptionsQuery());

/** The key check is free and quick; a switch that hangs longer is given up on. */
const SWITCH_TIMEOUT_MS = 15_000;

// The server may still finish the switch; the top bar refreshes to show what was saved.
const SWITCH_TIMED_OUT = `The key check took longer than ${SWITCH_TIMEOUT_MS / 1000} seconds. The top bar shows the AI model in use now.`;

const timedOut = (error: unknown) =>
  error instanceof ApiError &&
  error.cause instanceof DOMException &&
  error.cause.name === 'TimeoutError';

/** Switch the AI model. The server checks the key first; spend follows the active provider. */
export function useSwitchModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: modelKeys.switch,
    mutationFn: async ({ modelOptionId }: { modelOptionId: string }) => {
      try {
        return await unwrap(
          api.PUT('/api/scraping/model/active', {
            body: { modelOptionId },
            signal: AbortSignal.timeout(SWITCH_TIMEOUT_MS),
          }),
        );
      } catch (error) {
        throw timedOut(error)
          ? new ApiError(0, { kind: 'ProviderUnreachable', reason: SWITCH_TIMED_OUT })
          : error;
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: modelKeys.all });
      void queryClient.invalidateQueries({ queryKey: auditKeys.all });
      void queryClient.invalidateQueries({ queryKey: runKeys.spend() });
    },
  });
}
